import React from 'react';
import { useTranslation } from 'react-i18next';

const About = () => {
    const { t } = useTranslation();

    return (
        <div className="page-transition">
            <div className="container mx-auto px-4 py-12 max-w-4xl">
                <h1 className="text-4xl font-bold mb-8 text-center">{t('about.title')}</h1>

                <div className="prose max-w-none">
                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">{t('about.mission.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('about.mission.p1')}
                        </p>
                        <p className="text-gray-700">
                            {t('about.mission.p2')}
                        </p>
                    </section>

                    <section className="mb-12 bg-blue-50 p-8 rounded-lg">
                        <h2 className="text-2xl font-semibold mb-4">{t('about.neutrality.title')}</h2>
                        <div className="space-y-3">
                            <div className="flex items-start">
                                <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-gray-700">
                                    <strong>{t('about.neutrality.noPreferenceTitle')}:</strong> {t('about.neutrality.noPreferenceText')}
                                </p>
                            </div>
                            <div className="flex items-start">
                                <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-gray-700">
                                    <strong>{t('about.neutrality.allMatchingTitle')}:</strong> {t('about.neutrality.allMatchingText')}
                                </p>
                            </div>
                            <div className="flex items-start">
                                <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-gray-700">
                                    <strong>{t('about.neutrality.evidenceBasedTitle')}:</strong> {t('about.neutrality.evidenceBasedText')}
                                </p>
                            </div>
                            <div className="flex items-start">
                                <svg className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-gray-700">
                                    <strong>{t('about.neutrality.transparentTitle')}:</strong> {t('about.neutrality.transparentText')}
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">{t('about.howItWorks.title')}</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            <div className="card">
                                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                                    1
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{t('about.howItWorks.step1Title')}</h3>
                                <p className="text-gray-600">
                                    {t('about.howItWorks.step1Desc')}
                                </p>
                            </div>
                            <div className="card">
                                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                                    2
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{t('about.howItWorks.step2Title')}</h3>
                                <p className="text-gray-600">
                                    {t('about.howItWorks.step2Desc')}
                                </p>
                            </div>
                            <div className="card">
                                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                                    3
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{t('about.howItWorks.step3Title')}</h3>
                                <p className="text-gray-600">
                                    {t('about.howItWorks.step3Desc')}
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">{t('about.professionals.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('about.professionals.intro')}
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                            <li>{t('about.professionals.roles.phlebologists')}</li>
                            <li>{t('about.professionals.roles.vascularSurgeons')}</li>
                            <li>{t('about.professionals.roles.woundCareSpecialists')}</li>
                            <li>{t('about.professionals.roles.physicalTherapists')}</li>
                            <li>{t('about.professionals.roles.fittersOrthotists')}</li>
                            <li>{t('about.professionals.roles.generalPractitioners')}</li>
                        </ul>
                    </section>

                    <section className="mb-12 bg-green-50 p-8 rounded-lg">
                        <h2 className="text-2xl font-semibold mb-4">{t('about.partners.title')}</h2>
                        <p className="text-gray-700 mb-4">
                            {t('about.partners.intro')}
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                            <li>{t('about.partners.points.quality')}</li>
                            <li>{t('about.partners.points.info')}</li>
                            <li>{t('about.partners.points.options')}</li>
                            <li>{t('about.partners.points.support')}</li>
                        </ul>
                    </section>

                    <section className="text-center bg-gray-50 p-8 rounded-lg">
                        <h2 className="text-2xl font-semibold mb-4">{t('about.contact.title')}</h2>
                        <p className="text-gray-700 mb-6">
                            {t('about.contact.text')}
                        </p>
                        <a
                            href="/contact"
                            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors font-semibold min-h-touch"
                        >
                            {t('about.contact.button')}
                        </a>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default About;
